// R5.2 — Layer 0: the intended-use statement (spec §1.1, §4 header).
//
// §1.3 makes this statement MANDATORY and NOT DISMISSIBLE, and §12.6 part 2 makes it the ONE allowlisted
// occurrence in the whole product of "candidate score" and "match outcomes" — allowlisted BY ID, which is why it
// renders through `Copy` like everything else and why no component may ever inline its words.
//
// ============================================================================================================== *
// 2026-08-23 — ONE STATEMENT, ONCE, AS A FOOTER (owner directive, verbatim):
//
//   "look at what is displayed. some things seem like you talking to yourself, not the user. display what is
//    relevant to the user and not how we derived it or the source. it sounds like you are justifying your approach
//    to a user. e.g. 'documentation score, not a match prediction' — the user knows that already, why repeat it."
//
// WHAT THIS SURFACE USED TO DO: render a sticky banner carrying `CS-INTENDED-USE-SHORT` ("Documentation review
// only — not a candidate score, not a match prediction"), a `CS-BETA-LABEL` chip beside it, AND the full
// `CS-INTENDED-USE` paragraph immediately underneath — three stacked epistemic statements, all above the fold,
// before a single word about the applicant's own CV. On a live Family Medicine read the same disclaimer idea
// reached the applicant FOUR times (this stack, plus the convention panel's claim and its footer) before any
// content about their document.
//
// WHAT IT DOES NOW: renders that statement EXACTLY ONCE, as an unobtrusive footer, at the foot of the read.
//
// NOTHING IS WEAKENED AND NOTHING IS DE-REGISTERED. §1.3's requirement is that the statement be present,
// undismissable and reachable on every read — which it is: there is no dismiss affordance, it is not behind a
// disclosure, and it renders on every branch of the surface. §12.6's contract is about what this product may never
// CLAIM; it says nothing about how many times a disclaimer is repeated on one screen, and repeating it three times
// never made it truer. Both `CS-INTENDED-USE` and `CS-INTENDED-USE-SHORT` still render (the short form as the
// region's accessible name, per T-A11Y-1's landmark requirement), and `CS-BETA-LABEL` still renders adjacent to
// the short form, exactly as §8.3's rollout note requires — the whole group simply moved down the page and into
// one block.
//
// WHY THE COMPONENT NAMED `IntendedUseHeader` NOW CARRIES THE FOOTER: `RubricReadView.tsx` owns both call sites
// and is frozen for this change. It renders `<IntendedUseHeader beta={beta} /><IntendedUse />` as an adjacent
// pair, and `beta` is passed to the FIRST of the two only. Collapsing the pair therefore means the surviving
// block has to be the one that receives `beta`. The names are stale; the behaviour is the directive. `IntendedUse`
// is kept as an exported no-op rather than deleted, so neither import in `RubricReadView` breaks.
// ============================================================================================================== *
import { Copy } from './renderContent';
import { MUTED_STYLE, NOTICE_STYLE, SR_ONLY } from './styles';

/** The footer block: muted, rule-topped, at the end of the read rather than stacked above it. */
const FOOTER_STYLE = Object.freeze({
  ...NOTICE_STYLE,
  ...MUTED_STYLE,
  margin: 'var(--space-5) 0 0',
  padding: 'var(--space-3)',
  background: 'transparent',
  border: 'none',
  borderTop: '1px solid var(--rule)',
  borderRadius: 0,
});

/**
 * THE ONE INTENDED-USE BLOCK PER REVIEW. Despite the name (see the header note), this is the footer, and it is the
 * only thing either of this module's two exports puts on screen.
 *
 * `CS-BETA-LABEL` sits ADJACENT to `CS-INTENDED-USE-SHORT` (§8.3's rollout note), not in place of it — the short
 * form is the region's accessible name, so the chip is adjacent to it in the accessibility tree as well as in the
 * markup. Rendered as a plain region, never a dismissable modal: there is no dismiss affordance.
 */
export function IntendedUseHeader({ beta }: { beta: boolean }) {
  return (
    <section
      aria-labelledby="rubric-intended-use"
      style={FOOTER_STYLE}
      data-testid="rubric-intended-use"
    >
      {/* The region needs an accessible name (T-A11Y-1's landmark requirement) and the surface may not invent a
          label for it, so the name IS the registered short statement — visually hidden, since the full statement
          it labels is right underneath and printing both would be a stutter, not information. This is also the
          reason the short form is no longer a visible banner: it said the same thing as the paragraph below it. */}
      <h2 id="rubric-intended-use" style={SR_ONLY}>
        <Copy id="CS-INTENDED-USE-SHORT" />
      </h2>
      <p style={{ margin: 0 }}>
        <Copy id="CS-INTENDED-USE" />
      </p>
      {beta && (
        <p style={{ margin: 'var(--space-2) 0 0' }} data-testid="rubric-beta-label">
          <Copy id="CS-BETA-LABEL" />
        </p>
      )}
    </section>
  );
}

/**
 * DELIBERATELY RENDERS NOTHING. It used to render the full statement as a second block directly beneath the
 * banner; the statement now renders once, above, inside `IntendedUseHeader`. The export survives because
 * `RubricReadView.tsx` imports and renders it at two call sites and is not this change's to edit — a component
 * that renders nothing is the honest way to remove a duplicate without reaching into a frozen file.
 */
export function IntendedUse() {
  return null;
}
